import React, { useState } from 'react';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4722F]/60 focus:border-[#D4722F]';

export const FaqSuggestForm = ({ content, audiences = [], initialQuestion = '' }) => {
  const [form, setForm] = useState({ question: initialQuestion, details: '', email: '', audience: '', website: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.question.trim().length < 10) {
      setError('Please write your question in a little more detail (at least 10 characters).');
      return;
    }
    setError('');
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/faq/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: form.question.trim(),
          details: form.details.trim() || null,
          email: form.email.trim() || null,
          audience: form.audience || null,
          website: form.website,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Something went wrong. Please try again.');
      }
      setStatus('done');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  if (status === 'done') {
    return (
      <div className="flex items-start gap-3 rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="faq-suggest-success">
        <CheckCircle2 className="w-6 h-6 text-[#D4722F] shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">{content.successHeading}</p>
          <p className="text-sm text-gray-400 mt-1">{content.successMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="faq-suggest-form" noValidate>
      <input type="text" value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" data-testid="faq-suggest-honeypot" />
      <label className="block">
        <span className="sr-only">{content.questionLabel}</span>
        <textarea
          data-testid="faq-suggest-question"
          value={form.question}
          onChange={set('question')}
          rows={2}
          maxLength={300}
          required
          placeholder={content.questionPlaceholder}
          className={`${inputCls} resize-none`}
        />
      </label>
      <textarea
        data-testid="faq-suggest-details"
        value={form.details}
        onChange={set('details')}
        rows={2}
        maxLength={1500}
        placeholder={content.detailsPlaceholder}
        className={`${inputCls} resize-none`}
      />
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="faq-suggest-email" type="email" value={form.email} onChange={set('email')} placeholder={content.emailPlaceholder} className={inputCls} />
        <select data-testid="faq-suggest-audience" value={form.audience} onChange={set('audience')} className={`${inputCls} [&>option]:text-[#1F1E1E]`}>
          <option value="">{content.audiencePlaceholder}</option>
          {audiences.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      {error && <p className="text-sm text-[#FFB48A]" role="alert" data-testid="faq-suggest-error">{error}</p>}
      <div className="flex flex-wrap items-center gap-4 pt-1">
        <button
          type="submit"
          data-testid="faq-suggest-submit"
          disabled={status === 'sending'}
          className="inline-flex items-center gap-2 bg-[#B5541B] hover:bg-[#9A4616] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full transition-colors"
        >
          {status === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {content.submitLabel}
        </button>
        <p className="text-xs text-gray-500 max-w-sm">{content.privacyNote}</p>
      </div>
    </form>
  );
};
